"use client";

import { useCallback, useEffect, useState } from "react";
import { aiGateway, AiGatewayError } from "@/lib/emailCleaner/aiGateway";
import {
  computeCleanupSuggestions,
  computeEmailHabits,
  computeInboxSummary,
  computeSenderSummaries,
  computeSpendingInsights,
  computeSubscriptionSummaries,
  computeDashboardStats,
  type ScanProgress,
} from "@/lib/emailCleaner/compute";
import { formatBytes } from "@/lib/emailCleaner/format";
import { listAttachmentsForQuery, downloadAttachment, performMessageAction, listSample, type GmailAttachmentMeta } from "@/lib/emailCleaner/gmail";
import { rulesStore, senderFlagsStore } from "@/lib/emailCleaner/stores";
import { ruleSearchQuery } from "@/lib/emailCleaner/queries";
import { useBulkActionWithProgress } from "@/lib/emailCleaner/useBulkActionWithProgress";
import { useViewStack } from "@/lib/emailCleaner/useViewStack";
import type {
  AutomationRule,
  DashboardStats,
  RealCleanupSuggestion,
  RuleActionType,
  RuleConditionType,
  SenderSummary,
  SubscriptionSummary,
} from "@/lib/emailCleaner/types";
import { BulkActionProgressOverlay, Card, EmptyState, PrimaryButton, ScanProgressBar, ScreenHeader } from "../Shared";
import AssistantPanel from "../AssistantPanel";

export type MoreView =
  | { name: "ai-assistant" }
  | { name: "ai-summary" }
  | { name: "ai-insights" }
  | { name: "ai-cleanup-coach" }
  | { name: "senders" }
  | { name: "subscriptions" }
  | { name: "attachments" }
  | { name: "automation-rules" }
  | { name: "create-rule" }
  | { name: "analytics" }
  | { name: "storage-analyzer" }
  | { name: "productivity" };

type AccountProps = { accessToken: string; accountEmail: string };
type Props = AccountProps & { initialView: MoreView; onExit: () => void };

export default function MoreTab({ accessToken, accountEmail, initialView, onExit }: Props) {
  const { view, depth, push, pop } = useViewStack<MoreView>(initialView);
  const back = depth > 1 ? pop : onExit;

  switch (view.name) {
    case "ai-assistant":
      return (
        <div>
          <ScreenHeader title="AI Assistant" onBack={back} />
          <AiAssistantHost accessToken={accessToken} accountEmail={accountEmail} />
        </div>
      );
    case "ai-summary":
      return <AiSummaryScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "ai-insights":
      return <AiInsightsScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "ai-cleanup-coach":
      return <AiCleanupCoachScreen accessToken={accessToken} onBack={back} />;
    case "senders":
      return <SendersScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "subscriptions":
      return <SubscriptionsScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "attachments":
      return <AttachmentsScreen accessToken={accessToken} onBack={back} />;
    case "automation-rules":
      return <AutomationRulesScreen accessToken={accessToken} onBack={back} onCreate={() => push({ name: "create-rule" })} />;
    case "create-rule":
      return <CreateRuleScreen onBack={back} onSaved={pop} />;
    case "analytics":
      return <AnalyticsScreen accessToken={accessToken} onBack={back} />;
    case "storage-analyzer":
      return <StorageAnalyzerScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "productivity":
      return <ProductivityScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
  }
}

function AiAssistantHost({ accessToken, accountEmail }: AccountProps) {
  const [emails, setEmails] = useState<Awaited<ReturnType<typeof listSample>>>([]);
  useEffect(() => {
    listSample(accessToken, accountEmail, "", 40).then(setEmails);
  }, [accessToken, accountEmail]);
  return <AssistantPanel emails={emails} />;
}

function AiSummaryScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [scope, setScope] = useState<"daily" | "weekly">("daily");
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (s: "daily" | "weekly") => {
      setSummary(null);
      setError(null);
      computeInboxSummary(accessToken, accountEmail, s)
        .then(setSummary)
        .catch((err) => setError(err instanceof Error ? err.message : "Could not generate summary"));
    },
    [accessToken, accountEmail],
  );

  useEffect(() => {
    // Regenerating the summary whenever the scope changes is the intended sync effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(scope);
  }, [scope, load]);

  return (
    <div>
      <ScreenHeader title="AI Summary" onBack={onBack} />
      <div className="mb-4 flex gap-2">
        <button type="button" onClick={() => setScope("daily")} className={`flex-1 rounded-full py-1.5 text-sm font-semibold ${scope === "daily" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}>
          Today
        </button>
        <button type="button" onClick={() => setScope("weekly")} className={`flex-1 rounded-full py-1.5 text-sm font-semibold ${scope === "weekly" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}>
          This Week
        </button>
      </div>
      <Card>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {!error && !summary && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
        {summary && <p className="text-sm text-slate-700 dark:text-slate-300">{summary}</p>}
      </Card>
    </div>
  );
}

function AiInsightsScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [tab, setTab] = useState<"spending" | "subscriptions" | "habits" | "cleanup">("spending");
  return (
    <div>
      <ScreenHeader title="AI Insights" onBack={onBack} />
      <div className="mb-4 flex gap-2 overflow-x-auto">
        {(["spending", "subscriptions", "habits", "cleanup"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium capitalize ${tab === t ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "spending" && <SpendingTab accessToken={accessToken} accountEmail={accountEmail} />}
      {tab === "subscriptions" && <SubscriptionsTab accessToken={accessToken} accountEmail={accountEmail} />}
      {tab === "habits" && <HabitsTab accessToken={accessToken} accountEmail={accountEmail} />}
      {tab === "cleanup" && <CleanupTab accessToken={accessToken} />}
    </div>
  );
}

function SpendingTab({ accessToken, accountEmail }: AccountProps) {
  const [data, setData] = useState<{ categories: { name: string; amount: number }[]; total: number; insights: string[] } | null>(null);
  useEffect(() => {
    computeSpendingInsights(accessToken, accountEmail).then(setData);
  }, [accessToken, accountEmail]);
  if (!data) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  if (data.categories.length === 0 && data.insights.length === 0) return <EmptyState>No receipts found to analyze</EmptyState>;
  return (
    <div>
      <Card className="mb-3">
        <p className="font-semibold text-slate-900 dark:text-white">Estimated spend: ${data.total.toFixed(2)}</p>
      </Card>
      {data.categories.map((c) => (
        <Card key={c.name} className="mb-2 flex items-center justify-between">
          <span className="text-sm text-slate-700 dark:text-slate-300">{c.name}</span>
          <span className="text-sm font-semibold text-slate-900 dark:text-white">${c.amount.toFixed(2)}</span>
        </Card>
      ))}
      {data.insights.map((i, idx) => (
        <p key={idx} className="text-sm text-slate-600 dark:text-slate-400">• {i}</p>
      ))}
    </div>
  );
}

function SubscriptionsTab({ accessToken, accountEmail }: AccountProps) {
  const [subs, setSubs] = useState<SubscriptionSummary[] | null>(null);
  useEffect(() => {
    computeSubscriptionSummaries(accessToken, accountEmail).then(setSubs);
  }, [accessToken, accountEmail]);
  if (!subs) return <ScanProgressBar stage="Scanning for mailing lists…" current={0} total={1} />;
  if (subs.length === 0) return <EmptyState>No subscriptions detected</EmptyState>;
  return (
    <div>
      {subs.map((s) => (
        <Card key={s.senderEmail} className="mb-2">
          {s.senderName} — {s.emailCount} emails ({s.frequency})
        </Card>
      ))}
    </div>
  );
}

function HabitsTab({ accessToken, accountEmail }: AccountProps) {
  const [habits, setHabits] = useState<Awaited<ReturnType<typeof computeEmailHabits>> | null>(null);
  useEffect(() => {
    computeEmailHabits(accessToken, accountEmail).then(setHabits);
  }, [accessToken, accountEmail]);
  if (!habits) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  return (
    <div className="space-y-2">
      <Card>Busiest day: {habits.busiestDay}</Card>
      <Card>Average emails/day: {habits.avgPerDay}</Card>
      <Card>Unread ratio: {(habits.unreadRatio * 100).toFixed(0)}%</Card>
    </div>
  );
}

function CleanupTab({ accessToken }: { accessToken: string }) {
  const [suggestions, setSuggestions] = useState<RealCleanupSuggestion[] | null>(null);
  useEffect(() => {
    computeCleanupSuggestions(accessToken).then(setSuggestions);
  }, [accessToken]);
  if (!suggestions) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  if (suggestions.length === 0) return <EmptyState>Nothing to clean up right now</EmptyState>;
  return (
    <div>
      {suggestions.map((s) => (
        <Card key={s.category} className="mb-2">
          <p className="font-medium text-slate-900 dark:text-white">{s.category}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">{s.reason}</p>
          <p className="text-sm font-medium text-violet-600 dark:text-violet-400">
            {s.estimatedCount} emails • {formatBytes(s.estimatedBytes)}
          </p>
        </Card>
      ))}
    </div>
  );
}

function AiCleanupCoachScreen({ accessToken, onBack }: { accessToken: string; onBack: () => void }) {
  const [suggestions, setSuggestions] = useState<RealCleanupSuggestion[] | null>(null);
  useEffect(() => {
    computeCleanupSuggestions(accessToken).then(setSuggestions);
  }, [accessToken]);

  const totalCount = suggestions?.reduce((a, s) => a + s.estimatedCount, 0) ?? 0;
  const totalBytes = suggestions?.reduce((a, s) => a + s.estimatedBytes, 0) ?? 0;

  return (
    <div>
      <ScreenHeader title="AI Cleanup Coach" onBack={onBack} />
      {!suggestions && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
      {suggestions && (
        <Card className="mb-4 text-sm text-slate-700 dark:text-slate-300">
          {suggestions.length === 0
            ? "Your inbox looks great — nothing urgent to clean up."
            : `We found ${totalCount} emails that can safely be cleaned, recovering about ${formatBytes(totalBytes)}. Here's why:`}
        </Card>
      )}
      {suggestions?.map((s) => (
        <Card key={s.category} className="mb-2">
          <p className="font-medium text-slate-900 dark:text-white">{s.category}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">{s.reason}</p>
        </Card>
      ))}
    </div>
  );
}

function SendersScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [senders, setSenders] = useState<SenderSummary[] | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const { run, progress: bulkProgress } = useBulkActionWithProgress(accessToken);

  const load = useCallback(() => {
    computeSenderSummaries(accessToken, accountEmail).then(setSenders);
  }, [accessToken, accountEmail]);

  useEffect(() => {
    load();
  }, [load]);

  async function bulkAction(sender: SenderSummary, action: "trash" | "archive") {
    setMenuFor(null);
    const count = await run({ query: `from:${sender.email}`, actionType: action, historyLabel: sender.name, estimatedCount: sender.count });
    if (count === null) return;
    load();
  }

  return (
    <div>
      <BulkActionProgressOverlay progress={bulkProgress} />
      <ScreenHeader title="Senders" onBack={onBack} />
      {!senders && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
      {senders && senders.length === 0 && <EmptyState>No senders found</EmptyState>}
      {senders?.map((s) => (
        <Card key={s.email} className="mb-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-medium text-slate-900 dark:text-white">{s.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {s.count} emails • {formatBytes(s.estimatedBytes)}
              </p>
            </div>
            <button type="button" onClick={() => setMenuFor(menuFor === s.email ? null : s.email)} className="shrink-0 text-slate-400">
              ⋮
            </button>
          </div>
          {menuFor === s.email && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
              <button type="button" onClick={() => bulkAction(s, "trash")} className="rounded-full border border-red-300 px-3 py-1 text-xs text-red-600 dark:border-red-700 dark:text-red-400">
                Delete all
              </button>
              <button type="button" onClick={() => bulkAction(s, "archive")} className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300">
                Archive all
              </button>
              <button
                type="button"
                onClick={() => {
                  senderFlagsStore.setFlag(s.email, { muted: true });
                  setMenuFor(null);
                }}
                className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300"
              >
                Mute
              </button>
              <button
                type="button"
                onClick={() => {
                  senderFlagsStore.setFlag(s.email, { protected: true });
                  setMenuFor(null);
                }}
                className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300"
              >
                Protect
              </button>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function SubscriptionsScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [subs, setSubs] = useState<SubscriptionSummary[] | null>(null);
  const [progress, setProgress] = useState<ScanProgress | null>(null);

  useEffect(() => {
    computeSubscriptionSummaries(accessToken, accountEmail, setProgress).then(setSubs);
  }, [accessToken, accountEmail]);

  return (
    <div>
      <ScreenHeader title="Subscriptions" onBack={onBack} />
      {!subs && progress && <ScanProgressBar {...progress} />}
      {subs && subs.length === 0 && <EmptyState>No subscriptions detected</EmptyState>}
      {subs?.map((s) => (
        <Card key={s.senderEmail} className="mb-2 flex items-center justify-between">
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-900 dark:text-white">{s.senderName}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{s.senderEmail}</p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{s.emailCount}</p>
            <p className="text-xs text-slate-400">{s.frequency}</p>
          </div>
        </Card>
      ))}
    </div>
  );
}

const CATEGORY_LABELS: Record<string, string> = { image: "Images", video: "Videos", pdf: "PDFs", document: "Docs", audio: "Audio", archive: "Archives", other: "Other" };

function AttachmentsScreen({ accessToken, onBack }: { accessToken: string; onBack: () => void }) {
  const [refs, setRefs] = useState<GmailAttachmentMeta[] | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    listAttachmentsForQuery(accessToken, "has:attachment", 40).then(setRefs);
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  async function open(ref: GmailAttachmentMeta) {
    setBusy(ref.attachmentId);
    try {
      const url = await downloadAttachment(accessToken, ref.messageId, ref.attachmentId, ref.mimeType);
      const a = document.createElement("a");
      a.href = url;
      a.download = ref.filename;
      a.click();
    } finally {
      setBusy(null);
    }
  }

  async function remove(ref: GmailAttachmentMeta) {
    if (!window.confirm(`Gmail attachments can only be removed by deleting the whole email ("${ref.filename}"). Continue?`)) return;
    await performMessageAction(accessToken, ref.messageId, "trash");
    load();
  }

  const categorize = (mimeType: string, filename: string) => {
    const ext = filename.split(".").pop()?.toLowerCase() ?? "";
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType.startsWith("video/")) return "video";
    if (mimeType.startsWith("audio/")) return "audio";
    if (mimeType.includes("pdf") || ext === "pdf") return "pdf";
    if (["zip", "rar", "7z"].includes(ext)) return "archive";
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) return "document";
    return "other";
  };

  const filtered = refs?.filter((r) => !filter || categorize(r.mimeType, r.filename) === filter) ?? null;

  return (
    <div>
      <ScreenHeader title="Attachments" onBack={onBack} />
      <div className="mb-4 flex gap-2 overflow-x-auto">
        <button type="button" onClick={() => setFilter(null)} className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${!filter ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}>
          All
        </button>
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${filter === key ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {!refs && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
      {filtered && filtered.length === 0 && <EmptyState>No attachments found</EmptyState>}
      {filtered?.map((r) => (
        <Card key={`${r.messageId}-${r.attachmentId}`} className="mb-2 flex items-center justify-between gap-3">
          <button type="button" onClick={() => open(r)} disabled={busy === r.attachmentId} className="min-w-0 flex-1 text-left">
            <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{r.filename}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {r.fromName} • {formatBytes(r.sizeBytes)}
            </p>
          </button>
          <button type="button" onClick={() => remove(r)} className="shrink-0 text-red-500">
            🗑
          </button>
        </Card>
      ))}
    </div>
  );
}

function AutomationRulesScreen({ accessToken, onBack, onCreate }: { accessToken: string; onBack: () => void; onCreate: () => void }) {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [running, setRunning] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const { run, progress: bulkProgress } = useBulkActionWithProgress(accessToken);

  const load = useCallback(() => setRules(rulesStore.load()), []);
  useEffect(() => {
    // Hydrating from localStorage (an external store) must happen after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  function toggle(id: string, isActive: boolean) {
    rulesStore.save(rules.map((r) => (r.id === id ? { ...r, isActive } : r)));
    load();
  }

  function remove(id: string) {
    rulesStore.save(rules.filter((r) => r.id !== id));
    load();
  }

  async function runNow(rule: AutomationRule) {
    setRunning(rule.id);
    let query = ruleSearchQuery(rule);
    if (rule.actionType === "deleteAfterDays") {
      const days = Number.parseInt(rule.actionValue, 10) || 30;
      query = `${query} older_than:${days}d`;
    }
    const actionType = rule.actionType === "deleteAfterDays" ? "trash" : rule.actionType === "moveToLabel" ? "moveToLabel" : "archive";
    const count = await run({
      query,
      actionType,
      historyLabel: rule.description,
      moveToLabelName: rule.actionType === "moveToLabel" ? rule.actionValue : undefined,
      skipConfirmation: true,
    });
    setRunning(null);
    setResult(`Applied to ${count ?? 0} matching emails`);
  }

  return (
    <div>
      <BulkActionProgressOverlay progress={bulkProgress} />
      <ScreenHeader
        title="Automation Rules"
        onBack={onBack}
        action={
          <button type="button" onClick={onCreate} className="rounded-full bg-violet-600 px-4 py-1.5 text-sm font-semibold text-white">
            + New
          </button>
        }
      />
      {result && <Card className="mb-4 text-sm text-emerald-700 dark:text-emerald-400">{result}</Card>}
      {rules.length === 0 && <EmptyState>No rules yet. Tap + New to create one.</EmptyState>}
      {rules.map((rule) => (
        <Card key={rule.id} className="mb-3">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 flex-1 text-sm font-medium text-slate-900 dark:text-white">{rule.description}</p>
            <input type="checkbox" checked={rule.isActive} onChange={(e) => toggle(rule.id, e.target.checked)} className="h-5 w-5 accent-violet-600" />
          </div>
          <div className="mt-3 flex justify-end gap-3">
            <button type="button" onClick={() => remove(rule.id)} className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Delete
            </button>
            <button type="button" onClick={() => runNow(rule)} disabled={running === rule.id} className="text-xs font-medium text-violet-600 disabled:opacity-60 dark:text-violet-400">
              {running === rule.id ? "Running…" : "Run Now"}
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function CreateRuleScreen({ onBack, onSaved }: { onBack: () => void; onSaved: () => void }) {
  const [instruction, setInstruction] = useState("");
  const [conditionType, setConditionType] = useState<RuleConditionType>("senderContains");
  const [conditionValue, setConditionValue] = useState("");
  const [actionType, setActionType] = useState<RuleActionType>("moveToLabel");
  const [actionValue, setActionValue] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    if (!instruction.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await aiGateway.generateRule(instruction);
      setConditionType(result.condition_type as RuleConditionType);
      setConditionValue(result.condition_value);
      setActionType(result.action_type as RuleActionType);
      setActionValue(result.action_value);
    } catch (err) {
      setError(err instanceof AiGatewayError ? err.message : "AI rule generation failed");
    } finally {
      setGenerating(false);
    }
  }

  function save() {
    const description = instruction.trim() || `If ${conditionType} "${conditionValue}" then ${actionType}`;
    const rule: AutomationRule = {
      id: crypto.randomUUID(),
      description,
      conditionType,
      conditionValue: conditionValue.trim(),
      actionType,
      actionValue: actionValue.trim(),
      isActive: true,
    };
    rulesStore.save([...rulesStore.load(), rule]);
    onSaved();
  }

  return (
    <div>
      <ScreenHeader title="Create Rule" onBack={onBack} />
      <Card>
        <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Describe your rule in plain English</p>
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="e.g. If sender contains Amazon, move to Shopping"
          rows={2}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
        />
        <div className="mt-2">
          <PrimaryButton onClick={generate} disabled={generating}>
            {generating ? "Generating…" : "Generate with AI"}
          </PrimaryButton>
        </div>
        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </Card>

      <p className="mt-5 text-sm font-medium text-slate-700 dark:text-slate-300">Condition</p>
      <select
        value={conditionType}
        onChange={(e) => setConditionType(e.target.value as RuleConditionType)}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
      >
        {(["senderContains", "subjectContains", "isOtp", "isNewsletter", "isPromotion"] as RuleConditionType[]).map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <input
        value={conditionValue}
        onChange={(e) => setConditionValue(e.target.value)}
        placeholder="Condition value"
        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
      />

      <p className="mt-5 text-sm font-medium text-slate-700 dark:text-slate-300">Action</p>
      <select
        value={actionType}
        onChange={(e) => setActionType(e.target.value as RuleActionType)}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
      >
        {(["moveToLabel", "deleteAfterDays", "archiveOnRead"] as RuleActionType[]).map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <input
        value={actionValue}
        onChange={(e) => setActionValue(e.target.value)}
        placeholder="label name, or days for delete-after"
        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
      />

      <div className="mt-5">
        <PrimaryButton onClick={save}>Save Rule</PrimaryButton>
      </div>
    </div>
  );
}

function Bar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
        <span>{label}</span>
        <span>{value}</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800">
        <div className="h-full rounded-full bg-violet-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AnalyticsScreen({ accessToken, onBack }: { accessToken: string; onBack: () => void }) {
  const [suggestions, setSuggestions] = useState<RealCleanupSuggestion[] | null>(null);

  useEffect(() => {
    computeCleanupSuggestions(accessToken).then(setSuggestions);
  }, [accessToken]);

  const totalBytes = suggestions?.reduce((a, s) => a + s.estimatedBytes, 0) ?? 0;
  const maxBytes = Math.max(1, ...(suggestions?.map((s) => s.estimatedBytes) ?? [1]));

  return (
    <div>
      <ScreenHeader title="Analytics" onBack={onBack} />
      <Card className="mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">Storage Recoverable</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatBytes(totalBytes)}</p>
      </Card>
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-white">Storage Saved by Category</p>
      {!suggestions && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
      {suggestions?.length === 0 && <EmptyState>Run Smart Cleanup to see category data</EmptyState>}
      {suggestions?.map((s) => <Bar key={s.category} label={s.category} value={s.estimatedBytes} max={maxBytes} />)}
    </div>
  );
}

function StorageAnalyzerScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [senders, setSenders] = useState<SenderSummary[] | null>(null);
  const [suggestions, setSuggestions] = useState<RealCleanupSuggestion[] | null>(null);
  const [attachments, setAttachments] = useState<GmailAttachmentMeta[] | null>(null);

  useEffect(() => {
    computeDashboardStats(accessToken, accountEmail).then(setStats);
    computeSenderSummaries(accessToken, accountEmail).then(setSenders);
    computeCleanupSuggestions(accessToken).then(setSuggestions);
    listAttachmentsForQuery(accessToken, "has:attachment", 40).then(setAttachments);
  }, [accessToken, accountEmail]);

  const topSenders = [...(senders ?? [])].sort((a, b) => b.estimatedBytes - a.estimatedBytes).slice(0, 5);
  const topAttachments = [...(attachments ?? [])].sort((a, b) => b.sizeBytes - a.sizeBytes).slice(0, 5);

  return (
    <div>
      <ScreenHeader title="Storage Analyzer" onBack={onBack} />
      {stats && (
        <div className="mb-4 grid grid-cols-2 gap-3">
          <Card>
            <p className="text-xs text-slate-500 dark:text-slate-400">Total Used</p>
            <p className="text-lg font-bold text-slate-900 dark:text-white">{formatBytes(stats.estimatedStorageUsedBytes)}</p>
          </Card>
          <Card>
            <p className="text-xs text-slate-500 dark:text-slate-400">Recoverable</p>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatBytes(stats.estimatedRecoverableBytes)}</p>
          </Card>
        </div>
      )}

      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-white">Storage by Category</p>
      {suggestions?.map((s) => (
        <Card key={s.category} className="mb-2 flex items-center justify-between">
          <span className="text-sm text-slate-700 dark:text-slate-300">{s.category}</span>
          <span className="text-sm font-medium text-violet-600 dark:text-violet-400">{formatBytes(s.estimatedBytes)}</span>
        </Card>
      ))}

      <p className="mt-4 mb-2 text-sm font-semibold text-slate-900 dark:text-white">Largest Senders</p>
      {topSenders.map((s) => (
        <Card key={s.email} className="mb-2 flex items-center justify-between">
          <span className="truncate text-sm text-slate-700 dark:text-slate-300">{s.name}</span>
          <span className="shrink-0 text-sm font-medium text-violet-600 dark:text-violet-400">{formatBytes(s.estimatedBytes)}</span>
        </Card>
      ))}

      <p className="mt-4 mb-2 text-sm font-semibold text-slate-900 dark:text-white">Largest Attachments</p>
      {topAttachments.map((a) => (
        <Card key={`${a.messageId}-${a.attachmentId}`} className="mb-2 flex items-center justify-between">
          <span className="truncate text-sm text-slate-700 dark:text-slate-300">{a.filename}</span>
          <span className="shrink-0 text-sm font-medium text-violet-600 dark:text-violet-400">{formatBytes(a.sizeBytes)}</span>
        </Card>
      ))}
    </div>
  );
}

function ProductivityScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [habits, setHabits] = useState<Awaited<ReturnType<typeof computeEmailHabits>> | null>(null);
  useEffect(() => {
    computeEmailHabits(accessToken, accountEmail).then(setHabits);
  }, [accessToken, accountEmail]);

  return (
    <div>
      <ScreenHeader title="Productivity" onBack={onBack} />
      {!habits && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
      {habits && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <p className="text-xs text-slate-500 dark:text-slate-400">Emails / Day</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{habits.avgPerDay}</p>
            </Card>
            <Card>
              <p className="text-xs text-slate-500 dark:text-slate-400">Peak Time</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{habits.peakHour === null ? "-" : `${String(habits.peakHour).padStart(2, "0")}:00`}</p>
            </Card>
          </div>
          <Card>
            <p className="text-xs text-slate-500 dark:text-slate-400">Busiest Day</p>
            <p className="text-lg font-bold text-slate-900 dark:text-white">{habits.busiestDay}</p>
          </Card>
          <Card>
            <p className="text-xs text-slate-500 dark:text-slate-400">Unread Trend</p>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{(habits.unreadRatio * 100).toFixed(0)}% unread in your recent inbox</p>
          </Card>
        </div>
      )}
    </div>
  );
}
